"use client";

import { Html } from "@/Components";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { AppSectionProps } from "@/Templates/SectionsProps";
import _ from "lodash";
import { FC, ReactElement, ReactNode } from "react";
import HomeOneAppSectionGenerated, {
  HomeOneAppSectionGeneratedProps,
} from "../Sections/_generated/HomeOneAppSection.generated";
import HomeThirteenDownloadSectionGenerated, {
  HomeThirteenDownloadSectionGeneratedProps,
} from "../Sections/_generated/HomeThirteenDownloadSection.generated";
import HomeTwoAppSectionGenerated, {
  HomeTwoAppSectionGeneratedProps,
} from "../Sections/_generated/HomeTwoAppSection.generated";
import {
  buildGeneratedSectionProps,
  renderGeneratedSection,
} from "./generated-section-renderer";

type GeneratedAppRenderer = (props: AppSectionProps) => ReactElement;

const AppAppearance =
  CenterOptions.SiteTemplate.SiteTemplateSettings.SiteTemplateAppAppearance;

function renderHtmlValue(value?: string | null): ReactNode {
  if (!value) {
    return undefined;
  }

  return <Html value={value} />;
}

function getSectionImageUrl(props: AppSectionProps): string | undefined {
  return props.setting.Image?.Url ?? undefined;
}

function getSectionImageAlt(props: AppSectionProps, fallback: string): string {
  return props.setting.Image?.AltText ?? fallback;
}

function getDownloadItems(props: AppSectionProps) {
  if (_.isEmpty(props.lists.Default)) return undefined;
  return props.lists.Default.filter((item) => item.Image?.Url).map(
    (item, index) => ({
      key: item.Id,
      src: item.Image!.Url!,
      href: item.Link ?? "#",
      alt: item.Image?.AltText ?? `app-${index + 1}`,
    }),
  );
}

const generatedAppRenderers: Record<string, GeneratedAppRenderer> = {
  [AppAppearance.Home1.key]: (props) => {
    const defaults =
      buildGeneratedSectionProps<HomeOneAppSectionGeneratedProps>(
        "HomeOneAppSection",
        {
          sectionDiv0Div0Div0Div0Div0Div0H20H2Text:
            props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0Div0Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div0Div1Div0ImageWithBasePath0ImageAlt:
            getSectionImageAlt(props, "app"),
        },
      );

    return (
      <HomeOneAppSectionGenerated
        {...defaults}
        appSectionDivs={getDownloadItems(props) ?? defaults.appSectionDivs}
      />
    );
  },

  [AppAppearance.Home2.key]: (props) => {
    const defaults =
      buildGeneratedSectionProps<HomeTwoAppSectionGeneratedProps>(
        "HomeTwoAppSection",
        {
          sectionDiv0Div1Div0Div0Div0Div0H20H2Text:
            props.setting.Title ?? undefined,
          sectionDiv0Div1Div0Div0Div0Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div1Div0Div1Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div1Div0Div1Div0ImageWithBasePath0ImageAlt:
            getSectionImageAlt(props, "app"),
        },
      );

    return (
      <HomeTwoAppSectionGenerated
        {...defaults}
        appSectionDivs={getDownloadItems(props) ?? defaults.appSectionDivs}
      />
    );
  },

  [AppAppearance.Home13.key]: (props) => {
    const defaults =
      buildGeneratedSectionProps<HomeThirteenDownloadSectionGeneratedProps>(
        "HomeThirteenDownloadSection",
        {
          sectionDiv0Div0Div0Div0Div0H20H2Text:
            props.setting.Title ?? undefined,
          sectionDiv0Div0Div0Div0Div0P0PText: renderHtmlValue(
            props.setting.Description,
          ),
          sectionDiv0Div0Div0Div1Div0ImageWithBasePath0ImageSrc:
            getSectionImageUrl(props) ?? undefined,
          sectionDiv0Div0Div0Div1Div0ImageWithBasePath0ImageAlt:
            getSectionImageAlt(props, "app"),
        },
      );

    const downloadItems = getDownloadItems(props);

    return (
      <HomeThirteenDownloadSectionGenerated
        {...defaults}
        downloadSection1s={
          downloadItems?.map((item, index) => ({
            ...item,
            className: index === 0 ? "img-fluid img-1" : "img-fluid img-2",
          })) ?? defaults.downloadSection1s
        }
      />
    );
  },
};

const GeneratedAppSection: FC<AppSectionProps> = (props) => {
  return renderGeneratedSection(
    props,
    generatedAppRenderers,
    AppAppearance.Home1.key,
    (x) => x.setting.Appearance?.Key,
  );
};

export default GeneratedAppSection;
